import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { FormikSelect } from '../../../../components/Formik';
import useCloudQuota from '../../../../hooks/useCloudQuota';

import type { ComputeOrderValues, UnusedQuota } from '@/types/productTypes';

interface Cloud {
  name: string;
}

interface VirtualCloudData {
  cloud: Cloud;
  name: string;
  unusedQuota: UnusedQuota;
}

interface FormikSelectItem {
  id?: string | undefined;
  value?: string | undefined;
  label?: string | undefined;
}

const CloudSelect = (): React.JSX.Element => {
  const intl = useIntl();
  const { values, setValues } = useFormikContext<ComputeOrderValues>();
  const { availableVirtualClouds, getExternalNetworks } = useCloudQuota(values.juiceGroup);

  useEffect(() => {
    if (values.productId) {
      if (availableVirtualClouds.length === 0) {
        setValues(prev => ({
          ...prev,
          buttonHelperText: intl.formatMessage({ id: 'noVirtualCloudsForProduct' }),
        }));
      } else {
        setValues(prev => ({ ...prev, buttonHelperText: '' }));
      }
    }
  }, [values.productId, availableVirtualClouds.length, setValues, intl]);

  useEffect(() => {
    async function updateNetworks(): Promise<void> {
      if (values.cloudName) {
        const networks = await getExternalNetworks(values.cloudName);
        const cloudData = availableVirtualClouds.find((cloud: unknown) => {
          const typedCloud = cloud as VirtualCloudData;
          return typedCloud.cloud.name === values.cloudName || typedCloud.name === values.cloudName;
        }) as VirtualCloudData | undefined;

        setValues(prev => ({
          ...prev,
          externalNetworks: networks,
          unusedQuota: cloudData?.unusedQuota ?? {
            compute: { cores: 0, ram: 0 },
            volume: { gigabytes: 0 },
            network: { floatingIps: 0 },
          },
          buttonHelperText: '',
        }));
      }
    }
    updateNetworks();
  }, [values.cloudName, availableVirtualClouds, getExternalNetworks, setValues]);

  return (
    <FormikSelect
      name='cloudName'
      label={intl.formatMessage({ id: 'cloud' })}
      disabled={!values.productId}
      menuItems={availableVirtualClouds.map(virtualCloud => ({
        value: virtualCloud.cloud.name,
        label: virtualCloud.cloud.name,
      }))}
      onChange={(selected: FormikSelectItem) => {
        setValues({
          ...values,
          cloudName: selected.value ?? '',
        });
      }}
    />
  );
};

export default CloudSelect;
